/*
 * C:\Sovereign\Projects\Memorials\memorial-intake\Home.tsx
 *
 * HOME — Memorial Factory Intake Wizard
 * Design: "Quiet Reverence" — warm cream editorial
 * Fonts: Playfair Display (display) + DM Sans (body)
 * Layout: Single-column wizard, full-viewport steps, warm parchment background
 *
 * Steps:
 *   1 — About the Person (deceased info, tier, tone, contact)
 *   2 — Photos (drag-drop + tap, captions)
 *   3 — Video Clips (drag-drop + tap, captions)
 *   4 — Voice Tributes & Music (browser recorder + file upload)
 *   5 — Review & Submit
 *   ✓ — Confirmation
 *
 * Submission pipeline (Sovereign — no Stripe, no tRPC, no Express):
 *   1. Generate projectId client-side
 *   2. For each file: POST /api/upload/presign → PUT directly to R2
 *   3. POST /api/upload/complete with { projectId, manifest } → D1 row + webhook
 *   4. Show confirmation screen
 *
 * Worker contract (worker/src/routes/upload.ts):
 *   Presign response: { url, objectKey, tenant, bucket, expiresIn }
 *   Complete request: { projectId, manifest: { firstName, lastName, ... } }
 *   Complete response: { success, projectId, tenant, message }
 */

import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import {
  INITIAL_STATE,
  IntakeFormState,
  STEP_LABELS,
  TOTAL_STEPS,
  DeceasedInfo,
  PhotoAsset,
  VideoAsset,
  AudioAsset,
} from "./intake";
import Step1Info from "./Step1Info";
import Step2Photos from "./Step2Photos";
import Step3Videos from "./Step3Videos";
import Step4Audio from "./Step4Audio";
import Step5Review from "./Step5Review";
import StepConfirmation from "./StepConfirmation";
import { usePresignedUpload } from "./usePresignedUpload";
import { completeIntake } from "./src/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const BG_URL = "https://d2xsxph8kpxj0f.cloudfront.net/92373216/m4pBdcEoqG5JfKLSbSWXhK/memorial-hero-bg-nMRWo8ZnJZ94LShHBrDk82.webp";

const SESSION_KEY = "memorial_intake_state_v4";
const NOTES_KEY   = "memorial_intake_notes_v4";

// Clear ALL legacy session keys — v1 through v3 had broken blob-URL restore logic.
try {
  ["memorial_intake_state", "memorial_intake_notes",
   "memorial_intake_state_v2", "memorial_intake_notes_v2",
   "memorial_intake_state_v3", "memorial_intake_notes_v3"]
    .forEach(k => { sessionStorage.removeItem(k); localStorage.removeItem(k); });
} catch { /* ignore */ }

function loadState(): IntakeFormState {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.deceased?.firstName || parsed?.deceased?.primaryContactName) {
        // Only keep photos/videos that were already uploaded to R2 (have an objectKey).
        // Any asset without an objectKey had a blob URL that is now dead — discard it.
        // This prevents ERR_FILE_NOT_FOUND errors on page reload.
        const photos = (parsed.photos || [])
          .filter((p: PhotoAsset) => !!p.objectKey)
          .map((p: PhotoAsset) => ({ ...p, file: undefined, previewUrl: "/placeholder-photo.png" }));
        const videos = (parsed.videos || [])
          .filter((v: VideoAsset) => !!v.objectKey)
          .map((v: VideoAsset) => ({ ...v, file: undefined, previewUrl: "" }));
        const audio = (parsed.audio || []).map((a: AudioAsset) => ({
          ...a,
          file: undefined,
        }));
        return { ...INITIAL_STATE, ...parsed, photos, videos, audio, submitting: false, submitted: false };
      }
    }
  } catch { /* ignore */ }
  return INITIAL_STATE;
}

export default function Home() {
  const [state, setState]             = useState<IntakeFormState>(loadState);
  const [additionalNotes, setNotes]   = useState(() => localStorage.getItem(NOTES_KEY) || "");
  const [uploadProgress, setProgress] = useState(0);
  // Generate projectId at session start so usePresignedUpload always has a valid ID.
  // crypto.randomUUID() is available in all modern browsers and Vite dev server.
  const [projectId, setProjectId] = useState<string>(() => {
    const saved = state.projectId;
    if (saved && saved.length > 0) return saved;          // resume existing session
    return crypto.randomUUID();                           // new session
  });

  const { uploadFile } = usePresignedUpload(projectId);

  // Persist form state on every change
  useEffect(() => {
    try {
      const serializable = {
        ...state,
        projectId,
        photos: state.photos.map(p => ({ ...p, file: undefined, previewUrl: p.objectKey ? "" : p.previewUrl })),
        videos: state.videos.map(v => ({ ...v, file: undefined, previewUrl: v.objectKey ? "" : v.previewUrl })),
        audio:  state.audio.map(a  => ({ ...a, file: undefined })),
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(serializable));
    } catch { /* ignore */ }
  }, [state, projectId]);

  useEffect(() => {
    try { localStorage.setItem(NOTES_KEY, additionalNotes); } catch { /* ignore */ }
  }, [additionalNotes]);

  const setStep = (step: number) => setState(s => ({ ...s, step }));

  const canAdvance = useCallback((): boolean => {
    switch (state.step) {
      case 1:
        return !!(
          state.deceased.firstName.trim() &&
          state.deceased.lastName.trim() &&
          state.deceased.primaryContactName.trim() &&
          state.deceased.primaryContactEmail.trim()
        );
      case 2:
        return state.photos.length > 0;
      default:
        return true;
    }
  }, [state]);

  const advance = () => {
    if (!canAdvance()) {
      toast.error(
        state.step === 1
          ? "Please fill in the name and your contact information."
          : state.step === 2
          ? "Please add at least one photo to continue."
          : "Please complete this step to continue."
      );
      return;
    }
    setStep(state.step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStep(state.step - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /**
   * Upload all pending files to R2 via pre-signed PUT URLs.
   * Returns arrays of objectKeys for photos, videos, and audio.
   *
   * Worker presign response: { url, objectKey, tenant, bucket, expiresIn }
   * usePresignedUpload.uploadFile() returns { objectKey } (aligned in usePresignedUpload.ts).
   */
  const uploadAllAssets = async (pid: string): Promise<{
    photoKeys: string[];
    videoKeys: string[];
    audioKeys: string[];
  }> => {
    const allAssets = [
      ...state.photos.map(p => ({ file: p.file, objectKey: p.objectKey, assetType: "photos" as const })),
      ...state.videos.map(v => ({ file: v.file, objectKey: v.objectKey, assetType: "videos" as const })),
      ...state.audio.map(a  => ({ file: a.file, objectKey: a.objectKey, assetType: "audio"  as const })),
    ];

    const totalFiles = allAssets.filter(a => a.file).length;
    let uploaded = 0;

    const photoKeys: string[] = [];
    const videoKeys: string[] = [];
    const audioKeys: string[] = [];

    for (const asset of allAssets) {
      if (asset.objectKey) {
        // Already uploaded in a previous attempt — keep the key
        if (asset.assetType === "photos") photoKeys.push(asset.objectKey);
        if (asset.assetType === "videos") videoKeys.push(asset.objectKey);
        if (asset.assetType === "audio")  audioKeys.push(asset.objectKey);
        continue;
      }
      if (!asset.file) continue;

      const { objectKey } = await uploadFile(asset.file, asset.assetType, (pct) => {
        const base  = totalFiles > 0 ? (uploaded / totalFiles) * 100 : 0;
        const slice = totalFiles > 0 ? pct / totalFiles : 0;
        setProgress(Math.round(base + slice));
      });

      if (asset.assetType === "photos") photoKeys.push(objectKey);
      if (asset.assetType === "videos") videoKeys.push(objectKey);
      if (asset.assetType === "audio")  audioKeys.push(objectKey);
      uploaded++;
    }

    setProgress(100);
    return { photoKeys, videoKeys, audioKeys };
  };

  /**
   * Main submit handler — called by Step5Review "Submit" button.
   *
   * Flow:
   *   1. Generate projectId
   *   2. Upload all files directly to R2 via pre-signed PUT URLs
   *   3. POST /api/upload/complete with { projectId, manifest } → D1 upsert + webhook
   *   4. Show confirmation screen
   */
  const handleSubmit = async () => {
    setState(s => ({ ...s, submitting: true }));
    setProgress(0);

    const pid = projectId; // already generated at mount — stable for the whole session

    try {
      toast.info("Uploading your memories securely...");
      const { photoKeys, videoKeys, audioKeys } = await uploadAllAssets(pid);

      toast.info("Saving your order...");
      const result = await completeIntake({
        projectId: pid,
        manifest: {
          firstName:       state.deceased.firstName,
          lastName:        state.deceased.lastName,
          dateOfBirth:     state.deceased.dateOfBirth,
          dateOfPassing:   state.deceased.dateOfPassing,
          tagline:         state.deceased.tagline ?? "",
          tier:            state.deceased.tier,
          tone:            state.deceased.tone,
          musicStyle:      state.deceased.musicStyle ?? "",
          musicTrack:      state.deceased.musicTrack ?? "",
          contactName:     state.deceased.primaryContactName,
          contactEmail:    state.deceased.primaryContactEmail,
          contactPhone:    state.deceased.primaryContactPhone ?? "",
          photoOrderNotes: additionalNotes,
          photoKeys,
          videoKeys,
          audioKeys,
        },
      });

      if (!result.success) {
        throw new Error(result.message ?? "Intake submission failed");
      }

      // Clear persisted state on success
      try {
        localStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(NOTES_KEY);
      } catch { /* ignore */ }

      setState(s => ({ ...s, submitting: false, submitted: true, projectId: pid }));
    } catch (err) {
      console.error("[Submit] Failed:", err);
      toast.error("Something went wrong. Please try again.");
      setState(s => ({ ...s, submitting: false }));
      setProgress(0);
    }
  };

  /**
   * Paid checkout path — upload files to R2, then create a Stripe Checkout session.
   * The browser is redirected to Stripe's hosted checkout page.
   * On success, Stripe redirects back to /intake?session_id=...&project_id=...
   * which triggers verifySession and marks the order as paid.
   */
  const handleCheckout = async () => {
    setState(s => ({ ...s, submitting: true }));
    setProgress(0);
    const pid = projectId;
    try {
      toast.info("Uploading your memories securely...");
      const { photoKeys, videoKeys, audioKeys } = await uploadAllAssets(pid);

      // Save a partial D1 record so the order exists before Stripe redirect
      toast.info("Preparing checkout...");
      await completeIntake({
        projectId: pid,
        manifest: {
          firstName:       state.deceased.firstName,
          lastName:        state.deceased.lastName,
          dateOfBirth:     state.deceased.dateOfBirth,
          dateOfPassing:   state.deceased.dateOfPassing,
          tagline:         state.deceased.tagline ?? "",
          tier:            state.deceased.tier,
          tone:            state.deceased.tone,
          musicStyle:      state.deceased.musicStyle ?? "",
          musicTrack:      state.deceased.musicTrack ?? "",
          contactName:     state.deceased.primaryContactName,
          contactEmail:    state.deceased.primaryContactEmail,
          contactPhone:    state.deceased.primaryContactPhone ?? "",
          photoOrderNotes: additionalNotes,
          photoKeys,
          videoKeys,
          audioKeys,
          paymentStatus:   "pending",
        },
      });

      // Create Stripe Checkout session
      const origin = window.location.origin;
      const res = await fetch("/api/stripe/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId:    pid,
          tier:         state.deceased.tier,
          contactEmail: state.deceased.primaryContactEmail,
          contactName:  state.deceased.primaryContactName,
          successUrl:   `${origin}/intake`,
          cancelUrl:    `${origin}/intake`,
        }),
      });

      if (!res.ok) {
        const err = await res.json() as { error?: string };
        throw new Error(err.error ?? `Checkout failed (${res.status})`);
      }

      const { url } = await res.json() as { url: string };
      // Redirect to Stripe — page leaves, no further React state needed
      window.location.href = url;
    } catch (err) {
      console.error("[Checkout] Failed:", err);
      toast.error(err instanceof Error ? err.message : "Checkout failed. Please try again.");
      setState(s => ({ ...s, submitting: false }));
      setProgress(0);
    }
  };

  const progressPct = ((state.step - 1) / TOTAL_STEPS) * 100;

  if (state.submitted) {
    return (
      <PageShell>
        <StepConfirmation
          projectId={projectId || state.projectId}
          email={state.deceased.primaryContactEmail}
          name={state.deceased.firstName}
        />
      </PageShell>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage:      `url(${BG_URL})`,
        backgroundSize:       "cover",
        backgroundPosition:   "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Overlay */}
      <div className="min-h-screen bg-background/70 backdrop-blur-[1px]">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/90 backdrop-blur-md border-b border-border">
          <div className="max-w-2xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-widest font-medium">Memorial Factory</p>
                <h1 className="font-['Playfair_Display'] font-700 text-lg text-foreground leading-tight">
                  {state.submitted ? "Order Received" : STEP_LABELS[state.step - 1]}
                </h1>
              </div>
              {!state.submitted && (
                <div className="text-right flex flex-col items-end gap-1">
                  <span className="text-xs text-muted-foreground">Step {state.step} of {TOTAL_STEPS}</span>
                  {state.step > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        try {
                          localStorage.removeItem(SESSION_KEY);
                          localStorage.removeItem(NOTES_KEY);
                        } catch { /* ignore */ }
                        setState(INITIAL_STATE);
                        setNotes("");
                        setProjectId("");
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="text-xs text-muted-foreground/60 hover:text-destructive transition underline underline-offset-2"
                    >
                      Start Over
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Progress Line */}
            {!state.submitted && (
              <div className="progress-line">
                <div className="progress-line-fill" style={{ width: `${progressPct}%` }} />
              </div>
            )}
          </div>
        </header>

        {/* Step Tabs */}
        {!state.submitted && (
          <div className="max-w-2xl mx-auto px-4 pt-3">
            <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
              {STEP_LABELS.map((label, i) => {
                const stepNum  = i + 1;
                const isActive = stepNum === state.step;
                const isDone   = stepNum < state.step;
                return (
                  <button
                    key={stepNum}
                    type="button"
                    onClick={() => {
                      if (stepNum < state.step) {
                        setStep(stepNum);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    disabled={stepNum > state.step}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : isDone
                        ? "bg-primary/15 text-primary cursor-pointer hover:bg-primary/25"
                        : "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
                    }`}
                  >
                    {isDone ? "✓ " : ""}{label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="max-w-2xl mx-auto px-4 py-6 pb-32">
          {state.step === 1 && (
            <Step1Info
              data={state.deceased}
              onChange={(deceased: DeceasedInfo) => setState(s => ({ ...s, deceased }))}
            />
          )}
          {state.step === 2 && (
            <Step2Photos
              photos={state.photos}
              tier={state.deceased.tier}
              onChange={(photos: PhotoAsset[]) => setState(s => ({ ...s, photos }))}
            />
          )}
          {state.step === 3 && (
            <Step3Videos
              videos={state.videos}
              onChange={(videos: VideoAsset[]) => setState(s => ({ ...s, videos }))}
            />
          )}
          {state.step === 4 && (
            <Step4Audio
              audio={state.audio}
              onChange={(audio: AudioAsset[]) => setState(s => ({ ...s, audio }))}
              selectedMusicKey={state.deceased.musicTrack}
              selectedMusicTitle={state.deceased.musicTrackTitle}
              onMusicSelect={({ title, r2Key }) =>
                setState(s => ({
                  ...s,
                  deceased: {
                    ...s.deceased,
                    musicTrack: r2Key || undefined,
                    musicTrackTitle: title || undefined,
                  },
                }))
              }
            />
          )}
          {state.step === 5 && (
            <Step5Review
              state={state}
              additionalNotes={additionalNotes}
              onNotesChange={setNotes}
              onSubmit={handleSubmit}
              onCheckout={handleCheckout}
              submitting={state.submitting}
              uploadProgress={uploadProgress}
            />
          )}
        </main>

        {/* Bottom Navigation */}
        {!state.submitted && (
          <div className="fixed bottom-0 left-0 right-0 z-20 bg-background/95 backdrop-blur-md border-t border-border">
            <div className="max-w-2xl mx-auto px-4 py-3 flex gap-3">
              {state.step > 1 && (
                <button
                  type="button"
                  onClick={back}
                  disabled={state.submitting}
                  className="flex items-center gap-1.5 px-5 py-3 rounded-xl border-2 border-border text-foreground font-medium text-sm hover:border-primary/40 transition disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
              {state.step < TOTAL_STEPS && (
                <button
                  type="button"
                  onClick={advance}
                  disabled={state.submitting}
                  className="flex-1 flex items-center justify-center gap-1.5 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-40"
                >
                  Continue
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shell wrapper ─────────────────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
